import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Shield,
  CheckCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  FileText,
  AlertCircle,
  Star,
  Zap,
  Target,
  Award,
  Activity,
  BarChart3,
  Bell,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import { api } from '@/services/api';

interface ComplianceDashboard {
  currentPhase: string;
  overallScore: number;
  moduleScores: Record<string, number>;
  recentAlerts: Array<{
    id: string;
    type: 'info' | 'warning' | 'error' | 'critical';
    title: string;
    message: string;
    priority: 'low' | 'medium' | 'high' | 'critical';
    createdAt: string;
  }>;
  complianceTrend: Array<{
    date: string;
    score: number;
    phase: string;
  }>;
  lastReport: any;
}

interface ComplianceRule {
  id: string;
  module: string;
  phase: string;
  requirement: string;
  title: string;
  description: string;
  category: 'validation' | 'business' | 'security' | 'audit';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  version: string;
}

interface ComplianceCheck {
  id: string;
  ruleId: string;
  entityId: string;
  entityType: string;
  status: 'compliant' | 'non_compliant' | 'warning' | 'error';
  details: string;
  checkedAt: string;
  checkedBy: string;
  priority: 'low' | 'medium' | 'high' | 'critical';
  autoRemediation: boolean;
}

const PhasedCompliancePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'rules' | 'checks' | 'alerts' | 'reports'>('dashboard');
  const queryClient = useQueryClient();

  // Fetch compliance dashboard
  const { data: dashboardData } = useQuery<ComplianceDashboard>({
    queryKey: ['phased-compliance', 'dashboard'],
    queryFn: async () => {
      const res = await api.get('/api/phased-compliance/dashboard');
      return res.data.data;
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch compliance rules
  const { data: rulesData } = useQuery<{
    rules: ComplianceRule[];
    count: number;
  }>({
    queryKey: ['phased-compliance', 'rules'],
    queryFn: async () => {
      const res = await api.get('/api/phased-compliance/rules');
      return res.data;
    },
  });

  // Fetch compliance checks
  const { data: checksData } = useQuery<{
    checks: ComplianceCheck[];
    count: number;
  }>({
    queryKey: ['phased-compliance', 'checks'],
    queryFn: async () => {
      const res = await api.get('/api/phased-compliance/checks');
      return res.data;
    },
    refetchInterval: 15000, // Refresh every 15 seconds
  });

  // Fetch compliance alerts
  const { data: alertsData } = useQuery<{
    alerts: any[];
    count: number;
  }>({
    queryKey: ['phased-compliance', 'alerts'],
    queryFn: async () => {
      const res = await api.get('/api/phased-compliance/alerts');
      return res.data;
    },
    refetchInterval: 10000, // Refresh every 10 seconds
  });

  // Generate report mutation
  const generateReportMutation = useMutation({
    mutationFn: async (phase?: string) => {
      const res = await api.get('/api/phased-compliance/report', { 
        params: phase ? { phase } : {} 
      });
      return res.data.data;
    },
    onSuccess: () => {
      toast.success('Compliance report generated successfully');
      queryClient.invalidateQueries({ queryKey: ['phased-compliance'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to generate report');
    },
  });

  const getPhaseIcon = (phase: string) => {
    switch (phase) {
      case 'phase_1_basic':
        return <Star className="h-5 w-5" />;
      case 'phase_2_standard':
        return <Zap className="h-5 w-5" />;
      case 'phase_3_advanced':
        return <Target className="h-5 w-5" />;
      case 'phase_4_full':
        return <Award className="h-5 w-5" />;
      case 'phase_5_enterprise':
        return <Shield className="h-5 w-5" />;
      default:
        return <Star className="h-5 w-5" />;
    }
  };

  const getPhaseLabel = (phase: string) => {
    switch (phase) {
      case 'phase_1_basic':
        return 'Basic Compliance';
      case 'phase_2_standard':
        return 'Standard Compliance';
      case 'phase_3_advanced':
        return 'Advanced Compliance';
      case 'phase_4_full':
        return 'Full Compliance';
      case 'phase_5_enterprise':
        return 'Enterprise Compliance';
      default:
        return 'Unknown Phase';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 95) return 'text-green-600 bg-green-100';
    if (score >= 85) return 'text-blue-600 bg-blue-100';
    if (score >= 70) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'critical':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Bell className="h-4 w-4 text-blue-500" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'non_compliant':
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-600" />;
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'high':
        return 'bg-orange-100 text-orange-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Phased Compliance</h1>
          <p className="mt-1 text-sm text-gray-600">
            Monitor and manage regulatory compliance across all phases
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            onClick={() => generateReportMutation.mutate(undefined)}
            disabled={generateReportMutation.isPending}
            variant="primary"
          >
            <FileText className="h-4 w-4 mr-2" />
            {generateReportMutation.isPending ? 'Generating...' : 'Generate Report'}
          </Button>
        </div>
      </div>

      {/* Current Phase Overview */}
      {dashboardData && (
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl shadow-lg p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3">
                {getPhaseIcon(dashboardData.currentPhase)}
                <div>
                  <h2 className="text-2xl font-bold">{getPhaseLabel(dashboardData.currentPhase)}</h2>
                  <p className="text-blue-100">Current Compliance Phase</p>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex items-center space-x-2">
                  <span className="text-3xl font-bold">{dashboardData.overallScore.toFixed(1)}%</span>
                  <span className="text-blue-100">Overall Score</span>
                </div>
                <div className="mt-2 bg-blue-400 bg-opacity-30 rounded-full h-2">
                  <div 
                    className="bg-white rounded-full h-2 transition-all duration-300"
                    style={{ width: `${dashboardData.overallScore}%` }}
                  />
                </div>
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center space-x-2 text-blue-100">
                <Activity className="h-4 w-4" />
                <span className="text-sm">Last Updated</span>
              </div>
              <p className="text-white font-medium">
                {new Date().toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Module Scores */}
      {dashboardData && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Object.entries(dashboardData.moduleScores).map(([module, score]) => (
            <motion.div
              key={module}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl shadow-lg p-6"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 capitalize">
                    {module.replace('_', ' ')}
                  </p>
                  <p className={`text-2xl font-bold ${getScoreColor(score).split(' ')[0]}`}>
                    {score.toFixed(1)}%
                  </p>
                </div>
                <div className={`p-3 rounded-lg ${getScoreColor(score).split(' ')[1]}`}>
                  <Shield className="h-6 w-6" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-xl shadow-lg">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6">
            {[
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'rules', label: 'Rules', icon: Shield },
              { id: 'checks', label: 'Checks', icon: CheckCircle },
              { id: 'alerts', label: 'Alerts', icon: Bell },
              { id: 'reports', label: 'Reports', icon: FileText },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4 mr-2 inline" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'dashboard' && dashboardData && (
            <div className="space-y-6">
              {/* Recent Alerts */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Alerts</h3>
                <div className="space-y-3">
                  {dashboardData.recentAlerts.slice(0, 5).map((alert) => (
                    <div key={alert.id} className="flex items-center justify-between p-3 bg-white rounded-lg">
                      <div className="flex items-center space-x-3">
                        {getAlertIcon(alert.type)}
                        <div>
                          <p className="font-medium text-gray-900">{alert.title}</p>
                          <p className="text-sm text-gray-600">{alert.message}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(alert.priority)}`}>
                          {alert.priority}
                        </span>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(alert.createdAt).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Compliance Trend */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Compliance Trend</h3>
                <div className="space-y-3">
                  {dashboardData.complianceTrend.slice(-7).map((trend, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <TrendingUp className="h-4 w-4 text-green-600" />
                        <span className="text-sm text-gray-600">{trend.date}</span>
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`font-medium ${getScoreColor(trend.score).split(' ')[0]}`}>
                          {trend.score.toFixed(1)}%
                        </span>
                        <span className="text-sm text-gray-600">
                          {getPhaseLabel(trend.phase)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'rules' && rulesData && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Module</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phase</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Requirement</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rulesData.rules.map((rule) => (
                    <tr key={rule.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {rule.module.replace('_', ' ')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{rule.title}</p>
                          <p className="text-sm text-gray-500">{rule.description}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getPhaseIcon(rule.phase)}
                          <span className="ml-2 text-sm text-gray-900">
                            {getPhaseLabel(rule.phase)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          rule.requirement === 'mandatory' ? 'bg-red-100 text-red-800' :
                          rule.requirement === 'recommended' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {rule.requirement}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {rule.category}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          rule.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {rule.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'checks' && checksData && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entity</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Details</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Checked By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Checked At</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {checksData.checks.map((check) => (
                    <tr key={check.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {check.entityType} - {check.entityId}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {getStatusIcon(check.status)}
                          <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            check.status === 'compliant' ? 'bg-green-100 text-green-800' :
                            check.status === 'non_compliant' ? 'bg-red-100 text-red-800' :
                            check.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {check.status.replace('_', ' ')}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(check.priority)}`}>
                          {check.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {check.details}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {check.checkedBy}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(check.checkedAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'alerts' && alertsData && (
            <div className="space-y-4">
              {alertsData.alerts.map((alert) => (
                <div key={alert.id} className="bg-gray-50 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      {getAlertIcon(alert.type)}
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{alert.title}</h3>
                        <p className="text-sm text-gray-600">{alert.message}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(alert.priority)}`}>
                        {alert.priority}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        {new Date(alert.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'reports' && (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Compliance Reports</h3>
              <p className="text-gray-600 mb-4">
                Generate comprehensive compliance reports for any phase
              </p>
              <Button
                onClick={() => generateReportMutation.mutate(undefined)}
                disabled={generateReportMutation.isPending}
                variant="primary"
              >
                <FileText className="h-4 w-4 mr-2" />
                Generate Report
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhasedCompliancePage;
